import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import portfolioService from '../services/portfolio';
import { usePrivacy } from '../contexts/PrivacyContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const PortfolioList = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [portfolioName, setPortfolioName] = useState('');
  const { isPrivateMode } = usePrivacy();

  const { data: portfolios, isLoading, error, refetch } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.getPortfolios(),
  });

  const handleCreatePortfolio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!portfolioName.trim()) return;
    try {
      await portfolioService.createPortfolio({ name: portfolioName });
      setShowCreateModal(false);
      setPortfolioName('');
      refetch();
    } catch (error) {
      console.error('Failed to create portfolio', error);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <LoadingSpinner size="lg" text="Loading portfolios..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">Error loading portfolios</div>
    );
  }

  return (
    <div className="container-fluid py-4 px-0">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="fw-semibold mb-1" style={{ color: '#000537', letterSpacing: '-0.025em' }}>Portfolios</h1>
          <p style={{ color: '#71717a', fontSize: '0.875rem', margin: 0 }}>Track and manage your investments</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm d-flex align-items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"/>
          </svg>
          New Portfolio
        </button>
      </div>

      {portfolios && portfolios.length > 0 ? (
        <div className="row g-3">
          {portfolios.map((portfolio) => (
            <div key={portfolio.id} className="col-12 col-md-6 col-lg-4">
              <Link to={`/portfolios/${portfolio.id}?tab=performance`} className="text-decoration-none">
                <div className="card card-forest h-100" style={{ cursor: 'pointer' }}>
                  <div className="card-body">
                    <div className="fw-semibold mb-1" style={{ color: '#000537', fontSize: '0.95rem' }}>
                      {portfolio.name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#a1a1aa', marginBottom: '16px' }}>
                      {portfolio.entries?.length || 0} ETFs
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '0.8rem', color: '#71717a', fontWeight: 500 }}>View Details →</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center" style={{ border: '1px solid #f4f4f5', borderRadius: '12px', padding: '3rem' }}>
          <div style={{ color: '#d4d4d8', marginBottom: '12px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
              <path d="M7.5 1.018a7 7 0 0 0-4.79 11.566L7.5 7.793V1.018zm1 0V7.5h6.482A7.001 7.001 0 0 0 8.5 1.018zM14.982 8.5H8.207l-4.79 4.79A7 7 0 0 0 14.982 8.5zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8z"/>
            </svg>
          </div>
          <div className="fw-medium mb-2" style={{ color: '#18181b' }}>No portfolios yet</div>
          <div style={{ fontSize: '0.875rem', color: '#71717a', marginBottom: '20px' }}>
            Create your first portfolio to start tracking your ETF investments
          </div>
          <div>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm">
              Create Portfolio
            </button>
          </div>
        </div>
      )}

      {/* Create Portfolio Modal */}
      {showCreateModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ border: '1px solid #f4f4f5' }}>
                <div className="modal-header" style={{ borderBottom: '1px solid #f4f4f5', padding: '20px 24px 16px' }}>
                  <h5 className="modal-title fw-semibold" style={{ color: '#000537', fontSize: '1rem' }}>New Portfolio</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} aria-label="Close"></button>
                </div>
                <form onSubmit={handleCreatePortfolio}>
                  <div className="modal-body" style={{ padding: '20px 24px' }}>
                    <label htmlFor="portfolioName" className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#18181b' }}>
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="portfolioName"
                      value={portfolioName}
                      onChange={(e) => setPortfolioName(e.target.value)}
                      placeholder="My ETF Portfolio"
                      required
                    />
                  </div>
                  <div className="modal-footer" style={{ borderTop: '1px solid #f4f4f5', padding: '16px 24px', gap: '8px' }}>
                    <button type="button" className="btn btn-sm" style={{ border: '1px solid #e4e4e7', color: '#71717a', backgroundColor: 'white' }} onClick={() => setShowCreateModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">Create</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default PortfolioList;
